import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { ComponentType, JsonDataBlobComponentFragment } from '@graphql/generated';
import { JSON_COMPONENT_ACTIONS } from '../JsonComponent.constants';
import { useCallback } from 'react';
import { CUSTOM_PAGE_ACTIONS } from '../CustomComponents.constants';
import { useTranslation } from '@shared/core';
import { useWebsiteEditorCustomPageTelemetry } from '@website-editor/features/custom-page/WebsiteEditorCustomPage.telemetry';

/**
 * Navigates the Left Side Panel back to the main rearrange view
 */
export const useGoToReorderView = () => {
  const { navigateToScreen } = useNavigationPaneContext();

  const goToReorderView = useCallback(() => {
    navigateToScreen(CUSTOM_PAGE_ACTIONS.CUSTOM_COMPONENTS);
  }, [navigateToScreen]);

  return { goToReorderView };
};

export const useGoToDeleteModal = () => {
  const { setShowDeletePopup, setDeleteComponentDetails } = useCustomComponentStateContext<JsonDataBlobComponentFragment>();
  const { tq } = useTranslation('websiteEditor');

  const goToDeleteModal = useCallback(
    (data: JsonDataBlobComponentFragment) => {
      if (data.id) {
        setShowDeletePopup(true);
        setDeleteComponentDetails({
          id: data.id,
          title: tq`Are you sure you want to delete this section?`,
          subTitle: tq`The contents of this section will be lost. This action can’t be undone.`,
          deleteCta: tq`Delete Section`,
          cancelCta: tq`Cancel`,
          successMessage: tq`Success! Section has been deleted.`,
          errorMessage: tq`An error occurred while deleting this section. Please try again.`,
          type: data.containerContent.type ?? ComponentType.jsonDataBlob,
          accommodationPlaceId: data?.containerContent?.content?.accommodationPlaceId,
        });
      }
    },
    [setDeleteComponentDetails, setShowDeletePopup, tq],
  );

  return { goToDeleteModal };
};

/**
 * Navigates the Left Side Panel to the EditComponent view for the given ComponentData
 */
export const useGoToEditorView = () => {
  const { navigateToScreen } = useNavigationPaneContext();
  const { setCustomComponentBlockData } = useCustomComponentStateContext<JsonDataBlobComponentFragment>();
  const telemetry = useWebsiteEditorCustomPageTelemetry();

  const goToEditorView = useCallback(
    (componentData: JsonDataBlobComponentFragment) => {
      telemetry.goToEditViewClicked(componentData.id, componentData.containerContent.type || 'JsonDataBlobComponent');
      setCustomComponentBlockData(componentData);
      navigateToScreen(JSON_COMPONENT_ACTIONS.JSON_COMPONENT_EDIT);
    },
    [navigateToScreen, setCustomComponentBlockData, telemetry],
  );

  return { goToEditorView };
};

/**
 * Navigates the Left Side Panel to the CreateComponent view for the given ComponentData
 */
export const useGoToCreateView = () => {
  const { navigateToScreen } = useNavigationPaneContext();
  const { setCustomComponentBlockData } = useCustomComponentStateContext<JsonDataBlobComponentFragment>();

  const goToCreateView = useCallback(
    (componentData: JsonDataBlobComponentFragment) => {
      setCustomComponentBlockData(componentData);
      navigateToScreen(JSON_COMPONENT_ACTIONS.JSON_COMPONENT_ADD);
    },
    [navigateToScreen, setCustomComponentBlockData],
  );

  return { goToCreateView };
};
